import React from 'react';
import { FadeIn } from '../ui/FadeIn';
import { Book, Target, Wind, Sun, Moon, AlertCircle, CheckSquare } from 'lucide-react';
export function DetailsSection() {
  const contents = [{
    icon: <Book className="w-6 h-6" data-id="element-8" />,
    title: 'Plan de 14 días (1 página por día)',
    desc: 'Con intención, práctica y cierre.'
  }, {
    icon: <Target className="w-6 h-6" data-id="element-9" />,
    title: 'Página «Mis Intenciones»',
    desc: 'Para que empieces con claridad y compromiso amable.'
  }, {
    icon: <Wind className="w-6 h-6" data-id="element-10" />,
    title: 'Ejercicios de calma rápidos',
    desc: 'Respiración y técnicas simples para bajar el ruido mental.'
  }, {
    icon: <Sun className="w-6 h-6" data-id="element-11" />,
    title: 'Enfoque sin presión',
    desc: 'Aprendes a elegir una cosa importante y avanzar con un método realista.'
  }, {
    icon: <Moon className="w-6 h-6" data-id="element-12" />,
    title: 'Rutina de mañana y noche',
    desc: 'Para iniciar y cerrar el día con más paz.'
  }, {
    icon: <AlertCircle className="w-6 h-6" data-id="element-13" />,
    title: 'Plan de emergencia de 5 minutos',
    desc: 'Para esos días donde te sientes ansiosa, bloqueada o sobrepasada.'
  }, {
    icon: <CheckSquare className="w-6 h-6" data-id="element-14" />,
    title: 'Checklists + Tracker imprimible',
    desc: 'Para que lo completes y lo repitas cada vez que lo necesites.'
  }];
  return <section className="py-20 bg-[#FAF7F2]" data-id="element-15">
      <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-16">
        {/* What's Inside */}
        <div className="mb-24" data-id="element-17">
          <FadeIn data-id="element-18">
            <div className="text-center mb-16" data-id="element-19">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-20">
                Lo que vas a encontrar dentro
              </h2>
              <p className="text-xl text-[#6B5C4D] italic" data-id="element-21">
                (y por qué funciona)
              </p>
              <p className="mt-6 text-lg max-w-2xl mx-auto" data-id="element-22">
                Dentro del PDF vas a tener un camino guiado día por día. Cada
                día está diseñado para que lo puedas hacer incluso si estás
                ocupada, cansada o con poco ánimo.
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-23">
            {contents.map((item, index) => <FadeIn key={index} delay={index * 0.1} data-id="element-24">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#E8E0D5] h-full flex items-start gap-4 hover:shadow-md transition-shadow" data-id="element-25">
                  <div className="bg-[#FAF7F2] p-3 rounded-xl text-[#8B7355]" data-id="element-26">
                    {item.icon}
                  </div>
                  <div data-id="element-27">
                    <h4 className="font-bold text-lg text-[#8B7355] mb-2" data-id="element-28">
                      {item.title}
                    </h4>
                    <p className="text-[#6B5C4D]" data-id="element-29">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>

        {/* What it's NOT */}
        <div className="mb-24" data-id="element-30">
          <FadeIn data-id="element-31">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-[#E8E0D5]" data-id="element-32">
              <h3 className="text-3xl font-bold mb-8 text-center" data-id="element-33">
                Lo que NO es
              </h3>
              <p className="text-center text-[#6B5C4D] mb-8" data-id="element-34">
                (para que sepas exactamente qué estás comprando)
              </p>

              <div className="space-y-4 text-lg max-w-xl mx-auto" data-id="element-35">
                <p className="flex items-center gap-3" data-id="element-36">
                  <span className="w-2 h-2 rounded-full bg-[#8B7355]" data-id="element-37"></span>
                  No es terapia (aunque puede acompañarte).
                </p>
                <p className="flex items-center gap-3" data-id="element-38">
                  <span className="w-2 h-2 rounded-full bg-[#8B7355]" data-id="element-39"></span>
                  No promete "cambiar tu vida en 24 horas".
                </p>
                <p className="flex items-center gap-3" data-id="element-40">
                  <span className="w-2 h-2 rounded-full bg-[#8B7355]" data-id="element-41"></span>
                  No te exige hacerlo perfecto.
                </p>
              </div>

              <div className="mt-10 text-center" data-id="element-42">
                <p className="text-xl font-medium text-[#8B7355]" data-id="element-43">
                  Es un reinicio suave.
                </p>
                <p className="text-lg mt-2" data-id="element-44">
                  Un espacio diario para <strong data-id="element-45">volver a tu centro</strong> sin
                  castigarte.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Transformation */}
        <div data-id="element-46">
          <FadeIn data-id="element-47">
            <h3 className="text-4xl font-serif text-center text-[#8B7355] mb-12" data-id="element-48">
              Imagina esto...
            </h3>
            <p className="text-center text-xl mb-10" data-id="element-49">
              Imagínate llegar al día 14 y poder decir:
            </p>

            <div className="space-y-6 max-w-2xl mx-auto mb-12" data-id="element-50">
              <div className="bg-white/60 p-6 rounded-2xl italic text-lg text-center shadow-sm" data-id="element-51">
                "Ya sé cómo calmarme cuando mi mente se acelera."
              </div>
              <div className="bg-white/60 p-6 rounded-2xl italic text-lg text-center shadow-sm" data-id="element-52">
                "Ya sé cómo enfocarme en una sola cosa sin agotarme."
              </div>
              <div className="bg-white/60 p-6 rounded-2xl italic text-lg text-center shadow-sm" data-id="element-53">
                "Ya tengo una rutina simple que me sostiene."
              </div>
              <div className="bg-white/60 p-6 rounded-2xl italic text-lg text-center shadow-sm" data-id="element-54">
                "Ya no me abandono cuando estoy cansada."
              </div>
            </div>

            <div className="text-center text-xl" data-id="element-55">
              <p data-id="element-56">Eso es lo que estás construyendo:</p>
              <p className="font-serif text-2xl text-[#8B7355] mt-4" data-id="element-57">
                una forma más amorosa y firme de habitarte.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}